import { useLingui } from "@lingui/react";
import { getTrackingCategoryForMobilityAdProviderAd } from "@m10s/mobility-mapping-utils";
import { ElementType, EventType, Intent, TargetType } from "@mobility-item-page/utilities/client";
import { useEffect, useState } from "react";
import { useAdViewAd, useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { getPagingData, handlePagingKeyDown, type PagingData } from "src/utils/pagingData.ts";
import usePulse from "../../utils/pulse-tracking/usePulse.ts";
import { Icon } from "./Icon.tsx";

export const Paging = () => {
  const { i18n } = useLingui();

  const ad = useAdViewAd();
  const { adId } = useAdViewMeta();
  const { title } = useAdViewAd();
  const { trackClick } = usePulse();
  const [pagingData, setPagingData] = useState<PagingData | null>(null);

  useEffect(() => {
    const data = getPagingData(adId);
    setPagingData(data);

    if (data?.next) {
      const handleKeyDown = handlePagingKeyDown(data.next);

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [adId]);

  const text = {
    prev: i18n.t({
      id: "itemPageLayout.pagination.screenReader.previousAd",
      message: "Previous",
      comment: "Go to search - Screen reader - Navigation when coming from a search result",
    }),
    next: i18n.t({
      id: "itemPageLayout.pagination.screenReader.nextAd",
      message: "Next",
      comment: "Go to next ad - Screen reader - Navigation when coming from a search result",
    }),
    search: i18n.t({
      id: "itemPageLayout.pagination.screenReader.toSearch",
      message: "Go to Search",
      comment: "Go to previous ad - Screen reader - Navigation when coming from a search result",
    }),
  };

  return pagingData ? (
    <nav data-testid="objectpage-paging" className="ml-auto flex gap-x-16 justify-between">
      <a
        href={pagingData.search}
        rel="start"
        aria-label={text.search}
        data-testid="to-search"
        className="flex items-center gap-1"
        onClick={() =>
          // Tracking event: CE_ev52
          trackClick({
            name: "Search result",
            objectId: "CE_ev52",
            elementType: ElementType.Link,
            intent: Intent.Open,
            position: EventType.ClassifiedAd,
            target: {
              id: "pagination-go-to-search-results",
              type: TargetType.Listing,
              name: "Search result",
            },
          })
        }
      >
        <Icon name="ListSort" className="font-bold" />
      </a>
      {pagingData.prev && (
        <a
          href={pagingData.prev}
          rel="prev"
          aria-label={text.prev}
          data-testid="to-prev"
          className="flex items-center gap-1"
          onClick={() =>
            // Tracking event: CE_ev53
            trackClick({
              name: "Next ad",
              objectId: "CE_ev53",
              elementType: ElementType.Link,
              intent: Intent.Open,
              position: EventType.ClassifiedAd,
              target: {
                id: "pagination-go-to-next-ad",
                type: TargetType.ClassifiedAd,
                name: title ?? "n/a",
                contentId: adId.toString(),
                category: getTrackingCategoryForMobilityAdProviderAd(ad),
              },
            })
          }
        >
          <Icon name="ChevronLeft" className="font-bold" />
        </a>
      )}
      {pagingData.next && (
        <a
          href={pagingData.next}
          rel="next"
          aria-label={text.next}
          data-testid="to-next"
          className="flex items-center gap-1"
          onClick={() =>
            // Tracking event: CE_ev51
            trackClick({
              name: "Previous ad",
              objectId: "CE_ev51",
              elementType: ElementType.Link,
              intent: Intent.Open,
              position: EventType.ClassifiedAd,
              target: {
                id: "pagination-go-to-previous-ad",
                type: TargetType.ClassifiedAd,
                name: title ?? "n/a",
                contentId: adId.toString(),
                category: getTrackingCategoryForMobilityAdProviderAd(ad),
              },
            })
          }
        >
          <Icon name="ChevronRight" className="font-bold" />
        </a>
      )}
    </nav>
  ) : null;
};
